import {Injectable, NgZone} from '@angular/core'
import {HttpClient, HttpHeaders} from '@angular/common/http'
import {first, catchError, switchMap, mergeMap, timeout, retryWhen} from 'rxjs/operators'
import {Observable, throwError, timer} from 'rxjs'
import { ConfigService } from './config.service'
import { LocalStorageService } from './utils/local-storage.service'
import {fromPromise} from 'rxjs/internal-compatibility'
import {Router} from '@angular/router'
import { NativeEventSource, EventSourcePolyfill } from 'event-source-polyfill'
import {NavigateService} from './utils/navigate.service'

interface GenericRetryStrategyParams {
    maxRetryAttempts?: number
    scalingDuration?: number
    includedStatusCodes?: number[]
}

@Injectable({
    providedIn: 'root'
})
export class LbHttpService {

    public genericRetryStrategy =
        ( {
              maxRetryAttempts = 3, scalingDuration = 500, includedStatusCodes = [499, 503]
          }: GenericRetryStrategyParams = {} ) => ( attempts: Observable<any> ) => {
            return attempts.pipe(
                mergeMap( ( error, i ) => {
                    const retryAttempt = i + 1
                    if (
                        retryAttempt > maxRetryAttempts ||
                        !includedStatusCodes.find( e => e === error.status )
                    ) {
                        return throwError( error )
                    }
                    console.log( `Attempt ${retryAttempt}: retrying in ${retryAttempt * scalingDuration}ms` )
                    return timer( retryAttempt * scalingDuration )
                } )
            )
        }

    constructor(
        private zone: NgZone,
        private http: HttpClient,
        private config: ConfigService,
        private localStorage: LocalStorageService,
        // private navigateService: NavigateService,
        private router: Router,
    ){ }


    // ----------------------- CONNEXION -----------------------

    getApiKey( apiName ) {
        let url
        if ( apiName === 'booking' ) {
            url = this.config.getBookingApiKey
        } else if ( apiName === 'validation' ){
            url = this.config.getValidationApiKey
        }
        const httpOptions = {headers: this.generateHeaders( null, false )}

        return this.http.get(url, httpOptions).pipe(
            first(),
            catchError( (error) => {
                console.log( 'Error while getting API_KEY' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    async generateHeaderForLineberty( needLinebertyAccount, clearedHeaders = false, apiName ) {

        if ( !this.localStorage.issetApiKey( apiName ) ) {

            try {
                const res: any = await this.getApiKey( apiName ).toPromise()
                this.localStorage.setApiKey( apiName, res.apiKey )
            } catch (err) {
                this.localStorage.setApiKey( apiName, '' )
            }

        }

        const headers = {}

        const params = {
            'key': this.localStorage.getApiKey( apiName )
        }

        if ( needLinebertyAccount ) {

            try {
                await this.createOrLogUserIfNeeded( apiName )
            } catch (err) {
                console.log('Error while login the operator')
                console.log( err )
            }

            headers['Authorization'] = 'Bearer ' + this.localStorage.getBearer( apiName )

        }

        return { headers: this.generateHeaders(headers, clearedHeaders), params: params  }
    }

    generateHeaders( otherHeaders, clearedHeaders ) {
        let headers
        if ( otherHeaders ) {
            headers = otherHeaders
        } else {
            headers = {}
        }

        headers[ 'Access-Control-Allow-Methods' ] = 'POST,GET,OPTIONS,DELETE'
        headers[ 'Access-Control-Allow-Origin' ] = '*'
        headers[ 'Content-Type' ] = 'application/json'

        if ( clearedHeaders ) {
            return headers
        } else {
            return new HttpHeaders( headers )
        }
    }

    applyGetParamsToUrl (url , params ) {
        let firstParam = true
        for ( const k in params ) {
            if ( firstParam ) {
                url += '?'
                firstParam = false
            } else {
                url += '&'
            }
            if ( typeof( params[k] ) === 'object' ) {
                let firstParam2 = true
                for (const p in params[k]) {
                    if ( firstParam2 ) {
                        firstParam2 = false
                    } else {
                        url += '&'
                    }
                    url += k + '[]=' + params[k][p]
                }
            } else {
                url += k + '=' + params[k]
            }
        }

        return url
    }

    createUser() {
        const url = this.config.createUser
        const httpOptions = {headers: this.generateHeaders( null, false )}

        return this.http.post(url, null, httpOptions).pipe(
            timeout(this.config.defaultHttpTimeout),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while creating booking user' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    logUser( ) {
        const url = this.config.logUser
        const params = {userId: this.localStorage.getUserId()}
        const httpOptions = {headers: this.generateHeaders( null, false ), params: params}

        return this.http.get(url, httpOptions).pipe(
            timeout(this.config.defaultHttpTimeout),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while logging booking user' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    refreshToken( apiName ) {
        let url
        if ( apiName === 'booking' ) {
            url = this.config.refreshUserToken
        } else if ( apiName === 'validation' ) {
            url = this.config.refreshOperatorToken
        }

        const params = {userId: this.localStorage.getAccountId( apiName ), sessionId: this.localStorage.getSessionId( apiName )}
        const httpOptions = {headers: this.generateHeaders( null, false ), params: params}

        return this.http.get(url, httpOptions).pipe(
            timeout(this.config.defaultHttpTimeout),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while refreshing token' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    async createOrLogUserIfNeeded( apiName ) {

        if ( this.localStorage.issetBearer( apiName ) ) {

            if ( this.localStorage.tokenIsValid( apiName ) ) {

                if ( this.localStorage.needToRefreshToken( apiName ) ) {

                    try {
                        const res: any = await this.refreshToken( apiName ).toPromise()
                        console.log('Reshing Token res')
                        console.log( res )
                        this.localStorage.setBearer( apiName, res.jwtToken )
                    } catch ( err ) {
                        this.localStorage.setBearer( apiName, null )
                    }

                }

            } else {

                if ( apiName === 'booking' ) {

                    try {
                        const res: any = await this.logUser().toPromise()
                        console.log('Res after logging user')
                        console.log( res )
                        this.localStorage.setBearer( apiName, res.jwtToken )
                    } catch ( err ) {
                        this.localStorage.setBearer( apiName, null )
                    }

                } else if ( apiName === 'validation' ){
                    // return this.navigateService.goTo('login')
                }
            }

        } else {

            if ( apiName === 'booking' ) {
                try {
                    const res: any = await this.createUser().toPromise()
                    console.log('Res after create user')
                    console.log( res )
                    this.localStorage.setBearer( apiName, res.jwtToken )
                } catch ( err ) {
                    this.localStorage.setBearer( apiName, null )
                }

            } else if ( apiName === 'validation' ){
                // return this.navigateService.goTo('login')
            }

        }
    }

    // ----------------------- DATA -----------------------


    getPlace( placeId ) {
        let url = this.config.getPlace
        url = url.replace( '{placeId}', placeId )

        return fromPromise( this.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout(this.config.defaultHttpTimeout),
            switchMap(  ( httpOptions: any ) => {
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while getting place from placeId' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    getQueue( queueId ) {
        let url = this.config.getQueue
        url = url.replace( '{queueId}', queueId )

        return fromPromise( this.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout(this.config.defaultHttpTimeout),
            switchMap(  ( httpOptions: any ) => {
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while getting queue validation from queueId' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    getQueueBookingV1( queueId ) {
        let url = this.config.getQueueBookingV1
        url = url.replace( '{queueId}', queueId )

        return fromPromise( this.generateHeaderForLineberty( false, false, 'booking' ) ).pipe(
            timeout(this.config.defaultHttpTimeout),
            switchMap(  ( httpOptions: any ) => {
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while getting queue booking from queueId' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    getQueueBookingV2( queueId ): Observable<any> {
        let url = this.config.getQueueBookingV2
        url = url.replace( '{queueId}', queueId )

        return fromPromise( this.generateHeaderForLineberty( false, false, 'booking' ) ).pipe(
            timeout(this.config.defaultHttpTimeout),
            switchMap(  ( httpOptions: any ) => {
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while getting queue booking from queueId' )
                console.log(error)
                return throwError( error )
            })
        )
    }

    sendError ( error ) {
        const url = this.config.sendError
        const httpOptions = {headers: this.generateHeaders( null, false )}

        const body = {error: error}

        return this.http.post(url, body, httpOptions).pipe(
            timeout(this.config.defaultHttpTimeout),
            first(),
            retryWhen( this.genericRetryStrategy( {} ) ),
            catchError( (error) => {
                console.log( 'Error while sending error' )
                console.log(error)
                return throwError( error )
            })
        )
    }

}
